import axios from "axios";
import apiUrls from "src/api";
import type { IProjectLanguageData } from "src/model/model";

interface ApplyPldTranslationsParams {
  languageId: number;
  translations: Record<string, unknown>;
  contentType: string;
  plds: IProjectLanguageData[];
  mapContentKeysWithPldKeys: Record<string, [string, string][]>;
}

export interface ApplyPldTranslationsResult {
  pldId: number;
  mappedPayload: Record<string, unknown>;
}

/**
 * Maps content-keyed translation values onto the PLD-keyed shape and PUTs to
 * the project-language-data endpoint for the requested language. This bypasses
 * the active react-hook-form (which is bound to a single language) so that
 * non-active-language PLDs can be written directly.
 * Returns the target PLD id and mapped payload so callers can optimistically
 * patch Redux without a full graph refetch.
 */
export const applyPldTranslations = async ({
  languageId,
  translations,
  contentType,
  plds,
  mapContentKeysWithPldKeys,
}: ApplyPldTranslationsParams): Promise<ApplyPldTranslationsResult> => {
  const targetPld = plds.find((p) => Number(p.language) === Number(languageId));
  if (!targetPld || !(targetPld as any).id) {
    throw new Error(`No PLD found for language ${languageId}`);
  }

  const mapEntry = Object.entries(mapContentKeysWithPldKeys).find((entry) =>
    entry[0].includes(contentType),
  );
  if (!mapEntry) {
    throw new Error(`No PLD key map found for content type ${contentType}`);
  }
  const contentKeyToPldKey = new Map<string, string>();
  for (const [pldKey, contentKey] of mapEntry[1]) {
    contentKeyToPldKey.set(contentKey, pldKey);
  }

  const payload: Record<string, unknown> = {};
  for (const [contentKey, value] of Object.entries(translations)) {
    const pldKey = contentKeyToPldKey.get(contentKey);
    if (!pldKey) continue;
    payload[pldKey] = value;
  }

  if (Object.keys(payload).length === 0) {
    throw new Error("No translatable fields found in payload");
  }

  await axios.put(
    `${apiUrls.projects.projectLanguageDatas}/${(targetPld as any).id}`,
    { data: payload },
    { headers: { Authorization: `Bearer ${localStorage.getItem("wixar.auth.jwt")}` } },
  );

  return { pldId: (targetPld as any).id, mappedPayload: payload };
};
